import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicBoxComponent, MzicButtonComponent } from '@mzic/mzic-arpeggio-ui';
import { LanguageService } from '@mzic/mzic-services';
import { ArtistCatalogComponent } from '../../components/artist-catalog/artist-catalog.component';
import { HeaderComponent } from '../../components/header/header.component';

// Interface para oportunidades de investimento
interface InvestmentOpportunity {
  image: string;
  title: string;
  artist: string;
  categoryKey: 'phonogram' | 'catalog' | 'composition';
  historicalGainsMin: number;
  historicalGainsMax: number;
  url?: string; // URL opcional para roteamento
}

@Component({
  selector: 'mzic-investor-landing',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    TranslocoModule,
    RouterModule,
    MzicButtonComponent,
    MzicBoxComponent,
    ArtistCatalogComponent,
  ],
  templateUrl: './investor-landing.component.html',
  styleUrl: './investor-landing.component.scss',
  providers: [
    provideTranslocoScope({
      scope: 'investor-landing',
      alias: 'lang',
    }),
  ],
})
export class InvestorLandingComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  languageService = inject(LanguageService);
  activatedRoute = inject(ActivatedRoute);
  translocoService = inject(TranslocoService);
  router = inject(Router);
  private destroyRef = inject(DestroyRef);

  appStoreUrl = 'https://apps.apple.com/br/app/mzic/id1670495038';
  playStoreUrl = 'https://play.google.com/store/apps/details?id=com.mzic';

  // Social media links
  socialLinks = {
    instagram: 'https://www.instagram.com/mzicbrasil/',
    youtube: 'https://youtube.com/@mzicapp',
    spotify: 'https://spotify.com/mzic',
    linkedin: 'https://www.linkedin.com/company/mzic/',
  };

  // Footer links
  footerLinks = {
    forArtists: 'https://artists.mzic.io/',
    termsOfUse: 'https://legal.mzic.io/pt-br/terms-of-use',
    privacyPolicy: 'https://legal.mzic.io/pt-br/privacy',
    faqLearnMore: 'https://mzic.io/faq',
  };

  // App badges images
  googlePlayLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-google-play-icon.svg';
  appleLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-apple-icon.svg';
  googlePlayDownload = 'assets/images/googleplay-download.svg';
  appStoreDownload = 'assets/images/appstore-download.svg';

  // Partner logos
  transferoLogo = 'assets/images/transfero-logo.png';
  vbsoLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-vbso-icon.png';
  mercadoBitcoinLogo =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-mercado-bitcoin-icon.svg';
  cvmLogo = 'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-cvm-icon.svg';

  explanationSteps = [0, 1, 2]; // Step indices for i18n
  // Music explanation SVG icons
  explanationSvgIcons = [
    'assets/images/disco-with-play-slide1.svg',
    'assets/images/coins-slide2.svg',
    'assets/images/notebook-profit-slide3.svg',
  ];

  qrCodeMzic =
    'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-default-download-app-url.svg';

  activeFAQ: string | null = null;
  activePortfolioTab = 0;
  activeExplanationStep = 0;
  isChangingStep = false;
  isChangingPortfolioTab = false;

  portfolioTabs = [
    {
      icon: 'assets/icons/music-cd.svg',
      image: 'assets/images/portfolio-slide1.svg',
    },
    {
      icon: 'assets/icons/microphone2.svg',
      image: 'assets/images/portfolio-slide2.svg',
    },
    {
      icon: 'assets/icons/radio.svg',
      image: 'assets/images/portfolio-slide3.svg',
    },
  ];

  // Artist catalog data for the portfolio tabs
  artistCatalogData = [
    {
      displayMode: 'catalog' as const,
      artistImage:
        'https://mzic-website.s3.us-east-1.amazonaws.com/sofiaMarin.png',
      artistImageLink: '#',
      artistName: 'Sofia Marin',
      items: [
        {
          id: '1',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/linha-d-agua-small.svg',
          title: "Linha D'Água",
          subtitle: 'Sofia Marin',
        },
        {
          id: '2',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/loop-infinito-small.svg',
          title: 'Loop Infinito',
          subtitle: 'Sofia Marin',
        },
        {
          id: '3',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/luz-miuda-small.svg',
          title: 'Luz Miúda',
          subtitle: 'Sofia Marin',
        },
        {
          id: '4',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/carta-pra-ninguem-small.svg',
          title: 'Carta Para Ninguém',
          subtitle: 'Sofia Marin',
        },
        {
          id: '5',
          image:
            'https://mzic-website.s3.us-east-1.amazonaws.com/te-deixei-no-silencio-small.svg',
          title: 'Te Deixei no Silêncio',
          subtitle: 'Sofia Marin',
        },
      ],
    },
    {
      displayMode: 'image' as const,
      artistImage:
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-live-shows.png',
      artistImageLink: '#',
      artistName: '',
      items: [],
    },
    {
      displayMode: 'image' as const,
      artistImage:
        'https://mzic-website.s3.us-east-1.amazonaws.com/mzic-sofia-marin-catalogue.svg',
      artistImageLink: '#',
      artistName: '',
      items: [],
    },
  ];

  opportunities: InvestmentOpportunity[] = [
    {
      image: 'assets/images/portfolio-slide5.png',
      title: 'Vamo de Pagodin',
      artist: 'MC Daniel',
      categoryKey: 'composition',
      historicalGainsMin: 13.67,
      historicalGainsMax: 18.2,
      url: 'https://mzic.app.link/e/MZIC5',
    },
    {
      image: 'assets/images/portfolio-slide2.png',
      title: 'Manda um Oi',
      artist: 'Guilherme e Benuto, Simone Mendes',
      categoryKey: 'composition',
      historicalGainsMin: 20.72,
      historicalGainsMax: 23.47,
      url: 'https://mzic.app.link/e/MZIC1',
    },
    {
      image: 'assets/images/portfolio-slide3.png',
      title: 'Xonei',
      artist: 'Jorge & Mateus, Henrique & Juliano',
      categoryKey: 'composition',
      historicalGainsMin: 16.86,
      historicalGainsMax: 21.12,
      url: 'https://mzic.app.link/e/MZIC3',
    },
    {
      image: 'assets/images/portfolio-slide4.png',
      title: 'Equivocada',
      artist: 'Traia Véia, Gusttavo Lima',
      categoryKey: 'composition',
      historicalGainsMin: 13.29,
      historicalGainsMax: 18.44,
      url: 'https://mzic.app.link/e/MZIC4',
    },
    {
      image: 'assets/images/portfolio-slide1.png',
      title: 'DEUS É MUITO BOM',
      artist: 'Luan Santana',
      categoryKey: 'composition',
      historicalGainsMin: 15.85,
      historicalGainsMax: 17.29,
      url: 'https://mzic.app.link/e/MZIC2',
    },
  ];

  // Scroll sync properties
  private carouselScrollOffset = 0;
  private rafId: number | null = null;
  private lastScrollProgress = -1;

  // Section fade animation properties
  private sectionObserver: IntersectionObserver | null = null;
  private fadeableSections: string[] = [
    'hero-section',
    'broker-section',
    'investment-opportunities-section',
    'music-explanation-section',
    'portfolio-section',
    'app-download-section',
    'faq-section',
    'partners-section',
  ];

  // Swipe properties for explanation steps
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;
  private swipeDebounceTimeout: number | null = null;
  private swipeInitialized = false;
  private swipeRetryCount = 0;
  private swipeRetryTimeouts: number[] = [];
  private readonly MAX_SWIPE_INIT_RETRIES = 5;

  // Mathematical constants for swipe detection
  private readonly SWIPE_MIN_DISTANCE = 50; // Minimum pixels to consider as swipe
  private readonly SWIPE_MAX_TIME = 300; // Maximum milliseconds for swipe gesture
  private readonly SWIPE_MIN_VELOCITY = 0.1; // Minimum velocity (pixels/ms)
  private readonly SWIPE_ANGLE_THRESHOLD = 30; // Maximum angle deviation from horizontal (degrees)
  private readonly SWIPE_DEBOUNCE_TIME = 150; // Debounce time between swipes (ms)

  // Track FAQ and Partners sections for synchronized fade
  private faqSection: HTMLElement | null = null;
  private partnersSection: HTMLElement | null = null;
  private syncedSectionsState = {
    faqVisible: false,
    partnersVisible: false,
    lastGroupedVisibility: false,
  };

  constructor() {
    effect(() => {
      const lang = this.languageService.languageState();
      const current = this.router.url.split('/').pop();

      if (lang !== '') {
        this.router.navigate([`${lang}/${current}`]);
      }
    });
  }

  ngOnInit() {
    this.setLanguageFromRoute();
    this.preloadImages();
  }

  ngAfterViewInit() {
    this.initializeSectionFadeAnimation();

    // Initialize swipe with retry mechanism for better reliability
    this.initializeSwipeWithRetry();
  }

  /**
   * Preload all portfolio images to prevent loading delays when switching tabs
   * This improves UX by ensuring images are cached before user interaction
   */
  private preloadImages() {
    // Preload artist catalog images
    this.artistCatalogData.forEach((catalogData) => {
      const img = new Image();
      img.src = catalogData.artistImage;

      // Preload catalog item images if in catalog mode
      if (catalogData.displayMode === 'catalog' && catalogData.items.length > 0) {
        catalogData.items.forEach((item) => {
          const itemImg = new Image();
          itemImg.src = item.image;
        });
      }
    });

    // Preload portfolio tab images (for backward compatibility if needed)
    this.portfolioTabs.forEach((tab) => {
      const img = new Image();
      img.src = tab.image;
    });

    // Preload opportunity images
    this.opportunities.forEach((opportunity) => {
      const img = new Image();
      img.src = opportunity.image;
    });

    // Preload explanation SVG icons
    this.explanationSvgIcons.forEach((icon) => {
      const img = new Image();
      img.src = icon;
    });
  }

  ngOnDestroy() {
    // Cancel any pending animation frames for smooth performance
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Clear swipe debounce timeout
    if (this.swipeDebounceTimeout) {
      clearTimeout(this.swipeDebounceTimeout);
      this.swipeDebounceTimeout = null;
    }

    // Clear all retry timeouts
    this.swipeRetryTimeouts.forEach((id) => clearTimeout(id));
    this.swipeRetryTimeouts = [];

    // Clean up section observer
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }

    // Clean up swipe listeners
    if (this.swipeInitialized) {
      const targets = [
        '.music-explanation-section',
        '.explanation-content',
        '.explanation-image',
      ];

      for (const selector of targets) {
        const element = document.querySelector(selector);
        if (element) {
          this.removeSwipeListeners(element);
          break;
        }
      }
    }
  }

  setLanguageFromRoute() {
    this.activatedRoute.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const lang = params['lang'];
        if (lang) {
          this.translocoService.setActiveLang(lang);
          this.languageService.languageState.set(lang);
        }
      });
  }

  toggleFAQ(faqId: string) {
    if (this.activeFAQ === faqId) {
      this.activeFAQ = null;
    } else {
      this.activeFAQ = faqId;
    }
  }

  goToHeroCta() {
    this.scrollToAppDownloadSection();
  }

  scrollToAppDownloadSection() {
    const appDownloadSection = document.querySelector(
      '.app-download-section',
    ) as HTMLElement;

    if (!appDownloadSection) {
      console.warn('app-download-section not found');
      return;
    }

    // Get viewport dimensions
    const viewportHeight = window.innerHeight;

    // Get section position and dimensions
    const sectionRect = appDownloadSection.getBoundingClientRect();
    const sectionHeight = sectionRect.height;

    // Mathematical calculation to center the section in viewport
    // Current section top position relative to document
    const currentScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    const sectionTopFromDocument = sectionRect.top + currentScrollTop;

    // Calculate target scroll position to center the section
    // Formula: section_top - (viewport_height - section_height) / 2
    const targetScrollTop =
      sectionTopFromDocument - (viewportHeight - sectionHeight) / 2;

    // Ensure we don't scroll past the document bounds
    const maxScrollTop = document.documentElement.scrollHeight - viewportHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

    // Smooth scroll with enhanced easing for better UX
    window.scrollTo({
      top: finalScrollTop,
      behavior: 'smooth',
    });
  }

  goToAboutMzic() {
    const lang = this.languageService.languageState();
    if (lang !== '') {
      this.router.navigate([`${lang}/about`]);
    } else {
      this.router.navigate(['/about']);
    }
  }

  goToContact() {
    const lang = this.languageService.languageState();
    if (lang !== '') {
      this.router.navigate([`${lang}/contact`]);
    } else {
      this.router.navigate(['/contact']);
    }
  }

  onOpportunityClick(opportunity: InvestmentOpportunity) {
    if (opportunity.url) {
      window.open(opportunity.url, '_blank');
    }
    // Se não tem URL, não faz nada
  }

  setActivePortfolioTab(index: number) {
    if (this.activePortfolioTab !== index) {
      this.isChangingPortfolioTab = true;
      // Give time for the fade out to complete before changing the image
      setTimeout(() => {
        this.activePortfolioTab = index;
        // Reset the changing state after the new image starts loading
        setTimeout(() => {
          this.isChangingPortfolioTab = false;
        }, 50);
      }, 250);
    }
  }

  setActiveExplanationStep(index: number) {
    if (this.activeExplanationStep !== index) {
      this.isChangingStep = true;
      setTimeout(() => {
        this.activeExplanationStep = index;
        setTimeout(() => {
          this.isChangingStep = false;
        }, 50);
      }, 250);
    }
  }

  onImageLoad() {
    this.isChangingStep = false;
  }

  onPortfolioImageLoad() {
    // Only reset the changing state if we're currently changing
    if (this.isChangingPortfolioTab) {
      this.isChangingPortfolioTab = false;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Cancel previous animation frame if still pending
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    // Schedule smooth scroll update
    this.rafId = requestAnimationFrame(() => {
      this.syncCarouselScroll();
    });
  }

  private syncCarouselScroll() {
    const carouselTrack = document.querySelector(
      '.carousel-track',
    ) as HTMLElement;

    if (!carouselTrack) return;

    // Get carousel track dimensions and position
    const trackRect = carouselTrack.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate how much of the carousel-track is visible
    const trackTop = trackRect.top;
    const trackBottom = trackRect.bottom;
    const trackHeight = trackRect.height;

    // Check if carousel track is in viewport
    const isInViewport = trackTop < viewportHeight && trackBottom > 0;

    if (!isInViewport) {
      this.rafId = null;
      return;
    }

    // Calculate visible portion of the track
    const visibleTop = Math.max(0, trackTop);
    const visibleBottom = Math.min(viewportHeight, trackBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const visibilityPercentage = visibleHeight / trackHeight;

    // Start scrolling when 70% or more is visible (allows first image to show)
    if (visibilityPercentage < 0.7) {
      this.rafId = null;
      return;
    }

    // Enhanced mathematical scroll calculation for optimal last image visibility
    // Adapts to different screen sizes to ensure last image is always fully visible

    // Calculate card dimensions for different screen sizes
    const isDesktop = viewportHeight >= 768;
    const cardWidth = isDesktop ? 420 : 280; // Desktop vs Mobile card width
    const cardGap = isDesktop ? 40 : 20; // Gap between cards (2.5em = ~40px desktop, 20px mobile)

    // Calculate total scrollable distance needed to show last image completely
    const carouselWidth = carouselTrack.scrollWidth;
    const visibleWidth = carouselTrack.parentElement?.offsetWidth || 0;
    const maxScrollDistance = Math.max(0, carouselWidth - visibleWidth);

    // Calculate minimum scroll range needed to show last image
    // Last image needs to be fully visible = scroll distance + one card width
    const lastImageBuffer = cardWidth + cardGap; // Buffer to show last image completely
    const optimalScrollRange = maxScrollDistance + lastImageBuffer;

    // Responsive scroll range calculation:
    // Desktop: Larger range for smoother experience
    // Mobile: Smaller range for faster navigation
    const baseScrollRange = isDesktop
      ? viewportHeight * 1.8
      : viewportHeight * 1.4;
    const scrollRange = Math.max(baseScrollRange, optimalScrollRange);

    // Define scroll boundaries with different triggers for desktop and mobile
    // Desktop: 70% visibility (30% hidden) - PERFECT, don't change
    // Mobile: 90% visibility (10% hidden) - Start earlier for better user experience
    const visibilityTrigger = isDesktop
      ? trackHeight * 0.3 // Desktop: Start when 30% hidden (70% visible)
      : trackHeight * 0.1; // Mobile: Start when 10% hidden (90% visible)
    const scrollStart = viewportHeight - visibilityTrigger;

    // Calculate progress (0 to 1) ensuring last image is fully visible
    const rawProgress = (scrollStart - trackTop) / scrollRange;
    const scrollProgress = Math.max(0, Math.min(1, rawProgress));

    // Track progress for optimization

    // Mathematical guarantees:
    // 1. Starts at 70% visibility (first image partially visible)
    // 2. Extended range ensures last image is completely visible
    // 3. Adapts to screen size for optimal experience
    // 4. Maintains bidirectional consistency

    // Skip update if progress hasn't changed significantly (optimize performance)
    const progressDiff = Math.abs(scrollProgress - this.lastScrollProgress);
    if (progressDiff < 0.0001) {
      this.rafId = null;
      return;
    }

    this.lastScrollProgress = scrollProgress;

    // Only scroll if there's actual distance to scroll
    if (maxScrollDistance <= 0) {
      this.rafId = null;
      return;
    }

    // Calculate final carousel offset with enhanced range for last image visibility
    // Scale progress to ensure the last image is fully visible within our scroll range
    const scaledProgress = Math.min(
      1,
      scrollProgress * (optimalScrollRange / maxScrollDistance),
    );

    // Apply smooth easing while maintaining mathematical precision
    const easedProgress = this.easeInOutQuart(scaledProgress);
    this.carouselScrollOffset = easedProgress * maxScrollDistance;

    // Apply transformation with subpixel precision
    carouselTrack.style.willChange = 'transform';
    carouselTrack.style.transform = `translate3d(-${this.carouselScrollOffset.toFixed(2)}px, 0, 0)`;

    // Reset RAF id
    this.rafId = null;
  }

  private easeInOutQuart(t: number): number {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  }

  private initializeSectionFadeAnimation() {
    // Mathematical calculation for optimal fade thresholds
    // Desktop: More precise control, smoother transitions
    // Mobile: Faster transitions, less resource intensive
    const isMobile = window.innerWidth <= 768;
    const viewportHeight = window.innerHeight;

    // Calculate intersection thresholds mathematically
    // Desktop: More granular steps for smoother animation
    // Mobile: Fewer steps for better performance
    const thresholdSteps = isMobile ? 5 : 10;
    const thresholds = Array.from(
      { length: thresholdSteps },
      (_, i) => i / (thresholdSteps - 1),
    );

    // Mathematical root margin calculation
    // Creates a buffer zone based on viewport height
    // Desktop: Larger buffer for smoother experience
    // Mobile: Smaller buffer to avoid early triggering
    const rootMarginPercent = isMobile ? 6.75 : 4; // Percentage of viewport height (reduced by 20% for earlier fade-in)
    const rootMarginPx = Math.round(viewportHeight * (rootMarginPercent / 100));

    // Create observer with calculated mathematical parameters
    this.sectionObserver = new IntersectionObserver(
      (entries) => this.handleSectionIntersection(entries),
      {
        root: null, // Use viewport as root
        rootMargin: `-${rootMarginPx}px 0px -${rootMarginPx}px 0px`, // Symmetric margins
        threshold: thresholds, // Granular thresholds for smooth animation
      },
    );

    // Initialize sections with fade-out state
    // Add observer to each fadeable section
    setTimeout(() => {
      this.fadeableSections.forEach((sectionClass) => {
        const section = document.querySelector(
          `.${sectionClass}`,
        ) as HTMLElement;
        if (section) {
          // Initialize with fade-out state
          section.classList.add('section-fade-out');
          section.classList.remove('section-fade-in');

          // Observe the section
          this.sectionObserver?.observe(section);
        }
      });
    }, 100); // Small delay to ensure DOM is ready
  }

  private handleSectionIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      const section = entry.target as HTMLElement;
      const isFaqSection = section.classList.contains('faq-section');
      const isPartnersSection = section.classList.contains('partners-section');

      // Mathematical fade calculation based on intersection ratio
      const intersectionRatio = entry.intersectionRatio;

      // Calculate fade threshold mathematically
      const isMobile = window.innerWidth <= 768;
      const fadeInThreshold = isMobile ? 0.25 : 0.3;
      const fadeOutThreshold = isMobile ? 0.15 : 0.2;

      // Special handling for FAQ and Partners sections - synchronized fade
      if (isFaqSection || isPartnersSection) {
        this.handleGroupedSectionsFade(
          section,
          intersectionRatio,
          fadeInThreshold,
          fadeOutThreshold,
          isFaqSection,
        );
      } else {
        // Standard fade behavior for other sections
        if (intersectionRatio >= fadeInThreshold) {
          section.classList.add('section-fade-in');
          section.classList.remove('section-fade-out');
        } else if (intersectionRatio <= fadeOutThreshold) {
          section.classList.add('section-fade-out');
          section.classList.remove('section-fade-in');
        }
      }

      // Set CSS custom property for advanced animations
      section.style.setProperty(
        '--intersection-ratio',
        intersectionRatio.toString(),
      );
    });
  }

  private handleGroupedSectionsFade(
    section: HTMLElement,
    intersectionRatio: number,
    fadeInThreshold: number,
    fadeOutThreshold: number,
    isFaqSection: boolean,
  ) {
    // Update visibility state for the specific section
    if (isFaqSection) {
      this.syncedSectionsState.faqVisible =
        intersectionRatio >= fadeInThreshold;
      this.faqSection = section;
    } else {
      this.syncedSectionsState.partnersVisible =
        intersectionRatio >= fadeInThreshold;
      this.partnersSection = section;
    }

    // Mathematical calculation for grouped visibility:
    // Both sections fade together when either one reaches the threshold
    // This creates a visual grouping effect
    const groupedVisibility = this.calculateGroupedVisibility(
      this.syncedSectionsState.faqVisible,
      this.syncedSectionsState.partnersVisible,
      intersectionRatio,
      fadeInThreshold,
      fadeOutThreshold,
    );

    // Apply synchronized fade to both sections if they exist
    if (this.faqSection && this.partnersSection) {
      const shouldFadeIn = groupedVisibility;
      const shouldFadeOut = !groupedVisibility;

      // Apply fade state to both sections simultaneously
      [this.faqSection, this.partnersSection].forEach((targetSection) => {
        if (shouldFadeIn) {
          targetSection.classList.add('section-fade-in');
          targetSection.classList.remove('section-fade-out');
        } else if (shouldFadeOut) {
          targetSection.classList.add('section-fade-out');
          targetSection.classList.remove('section-fade-in');
        }
      });

      this.syncedSectionsState.lastGroupedVisibility = groupedVisibility;
    } else {
      // If partner section not yet available, handle individually
      if (intersectionRatio >= fadeInThreshold) {
        section.classList.add('section-fade-in');
        section.classList.remove('section-fade-out');
      } else if (intersectionRatio <= fadeOutThreshold) {
        section.classList.add('section-fade-out');
        section.classList.remove('section-fade-in');
      }
    }
  }

  private calculateGroupedVisibility(
    faqVisible: boolean,
    partnersVisible: boolean,
    currentRatio: number,
    fadeInThreshold: number,
    fadeOutThreshold: number,
  ): boolean {
    // Mathematical formula for grouped visibility:
    // Fade in when EITHER section reaches fadeInThreshold
    // Fade out only when BOTH sections are below fadeOutThreshold
    // This creates a "sticky" grouping effect

    const eitherVisible = faqVisible || partnersVisible;
    const bothInvisible = !faqVisible && !partnersVisible;

    // Hysteresis to prevent flickering:
    // Use different thresholds for fade in vs fade out
    if (this.syncedSectionsState.lastGroupedVisibility) {
      // Currently visible - require both to be invisible to fade out
      return !bothInvisible || currentRatio > fadeOutThreshold;
    } else {
      // Currently hidden - require either to be visible to fade in
      return eitherVisible && currentRatio >= fadeInThreshold;
    }
  }

  // Initialize swipe with retry mechanism for better reliability
  private initializeSwipeWithRetry() {
    // Only enable swipe on mobile devices
    if (window.innerWidth > 768) {
      this.swipeInitialized = true;
      return;
    }

    // Use requestAnimationFrame to ensure DOM is painted
    requestAnimationFrame(() => {
      const success = this.tryInitializeSwipe();

      if (!success && this.swipeRetryCount < this.MAX_SWIPE_INIT_RETRIES) {
        this.swipeRetryCount++;
        // Exponential backoff: 100ms, 200ms, 400ms, 800ms, 1600ms
        const delay = 100 * Math.pow(2, this.swipeRetryCount - 1);
        const timeoutId = window.setTimeout(
          () => this.initializeSwipeWithRetry(),
          delay,
        );
        this.swipeRetryTimeouts.push(timeoutId);
      }
    });
  }

  // Try to initialize swipe gestures
  private tryInitializeSwipe(): boolean {
    // If already initialized, don't reinitialize
    if (this.swipeInitialized) return true;

    // Try multiple selectors to find the target area
    const targets = [
      '.music-explanation-section',
      '.explanation-content',
      '.explanation-image',
    ];

    let swipeTarget: Element | null = null;
    for (const selector of targets) {
      swipeTarget = document.querySelector(selector);
      if (swipeTarget) break;
    }

    if (!swipeTarget) {
      console.debug(
        `Swipe init attempt ${this.swipeRetryCount + 1}: Target not found yet`,
      );
      return false;
    }

    // Verify element has dimensions (is rendered)
    const rect = swipeTarget.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.debug(
        `Swipe init attempt ${this.swipeRetryCount + 1}: Target has no dimensions yet`,
      );
      return false;
    }

    // Remove any existing listeners first to prevent duplicates
    this.removeSwipeListeners(swipeTarget);

    // Add touch event listeners with passive option for better performance
    swipeTarget.addEventListener('touchstart', this.handleTouchStart, {
      passive: false,
    });
    swipeTarget.addEventListener('touchmove', this.handleTouchMove, {
      passive: false,
    });
    swipeTarget.addEventListener('touchend', this.handleTouchEnd, {
      passive: true,
    });

    this.swipeInitialized = true;
    console.debug('Swipe gestures initialized successfully');
    return true;
  }

  // Remove swipe listeners (for cleanup)
  private removeSwipeListeners(element: Element) {
    element.removeEventListener('touchstart', this.handleTouchStart);
    element.removeEventListener('touchmove', this.handleTouchMove);
    element.removeEventListener('touchend', this.handleTouchEnd);
  }

  // Bound event handlers to preserve 'this' context
  private handleTouchStart = (e: Event) => this.onTouchStart(e as TouchEvent);
  private handleTouchMove = (e: Event) => this.onTouchMove(e as TouchEvent);
  private handleTouchEnd = (e: Event) => this.onTouchEnd(e as TouchEvent);

  private onTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) return; // Only handle single touch

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isSwiping = false;
  }

  private onTouchMove(event: TouchEvent) {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    // Check if this is likely a horizontal swipe
    if (deltaX > deltaY && deltaX > 10) {
      this.isSwiping = true;
      // Prevent vertical scrolling during horizontal swipe
      event.preventDefault();
    }
  }

  private onTouchEnd(event: TouchEvent) {
    if (!this.isSwiping || event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];
    const endTime = Date.now();

    // Calculate swipe metrics mathematically
    const swipeData = this.calculateSwipeMetrics(
      this.touchStartX,
      this.touchStartY,
      touch.clientX,
      touch.clientY,
      this.touchStartTime,
      endTime,
    );

    // Validate and process swipe
    if (this.isValidSwipe(swipeData)) {
      this.processSwipe(swipeData.direction as 'left' | 'right');
    }

    // Reset swipe state
    this.isSwiping = false;
  }

  private calculateSwipeMetrics(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    startTime: number,
    endTime: number,
  ) {
    // Calculate distance and direction vectors
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const time = endTime - startTime;
    const velocity = distance / time;

    // Calculate angle in degrees (0° = horizontal right, 90° = vertical up)
    const angle = Math.abs(
      (Math.atan2(deltaY, Math.abs(deltaX)) * 180) / Math.PI,
    );

    // Determine horizontal direction
    const direction = deltaX > 0 ? 'right' : 'left';

    return {
      deltaX: Math.abs(deltaX),
      deltaY: Math.abs(deltaY),
      distance,
      time,
      velocity,
      angle,
      direction,
    };
  }

  private isValidSwipe(swipeData: {
    deltaX: number;
    deltaY: number;
    distance: number;
    time: number;
    velocity: number;
    angle: number;
    direction: string;
  }): boolean {
    // Mathematical validation of swipe gesture
    return (
      swipeData.deltaX >= this.SWIPE_MIN_DISTANCE && // Minimum horizontal distance
      swipeData.time <= this.SWIPE_MAX_TIME && // Within time threshold
      swipeData.velocity >= this.SWIPE_MIN_VELOCITY && // Minimum velocity
      swipeData.angle <= this.SWIPE_ANGLE_THRESHOLD && // Primarily horizontal
      swipeData.deltaX > swipeData.deltaY // More horizontal than vertical
    );
  }

  private processSwipe(direction: 'left' | 'right') {
    // Prevent multiple rapid swipes with debounce
    if (this.swipeDebounceTimeout) return;

    // Calculate next step based on swipe direction
    let nextStep = this.activeExplanationStep;

    if (direction === 'left' && nextStep < this.explanationSteps.length - 1) {
      nextStep++;
    } else if (direction === 'right' && nextStep > 0) {
      nextStep--;
    } else {
      return; // No valid step change
    }

    // Apply the step change
    this.setActiveExplanationStep(nextStep);

    // Set debounce timeout to prevent rapid consecutive swipes
    this.swipeDebounceTimeout = window.setTimeout(() => {
      this.swipeDebounceTimeout = null;
    }, this.SWIPE_DEBOUNCE_TIME) as unknown as number;
  }

  /**
   * Formats a number to Portuguese locale with comma as decimal separator
   * Only displays decimals if the value has decimal places
   * @param value - The number to format
   * @returns Formatted string with comma as decimal separator (only when needed)
   */
  formatPercentage(value: number): string {
    const hasDecimals = value % 1 !== 0;
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }
}
